import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ExternalLink } from "lucide-react";

const ACTIVE_PREFIXES = ["/servicios/", "/stand/", "/alquiler/", "/proyecto-especial/", "/mis-espacios"];
const MAX_AGE_MS = 4 * 60 * 60 * 1000; // 4 h

function clearAdminMode() {
  try {
    sessionStorage.removeItem("am_admin_edit_mode");
    sessionStorage.removeItem("mis_espacios");
  } catch (e) {
    console.warn("clearAdminMode: sessionStorage unavailable", e);
  }
}

export default function AdminEditBanner() {
  const [mode, setMode] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const load = () => {
      try {
        const raw = sessionStorage.getItem("am_admin_edit_mode");
        if (!raw) { setMode(null); return; }
        const parsed = JSON.parse(raw);
        // Requisito 1: solo mostrar si el admin sigue logueado
        const token = localStorage.getItem("am_session_token");
        if (!token) { clearAdminMode(); setMode(null); return; }
        // Requisito 2: expirar entradas viejas (>4h) — evita banners "zombies" tras F5 al día siguiente
        const entryAt = parsed.entryAt ? new Date(parsed.entryAt).getTime() : 0;
        if (!entryAt || (Date.now() - entryAt) > MAX_AGE_MS) {
          clearAdminMode(); setMode(null); return;
        }
        setMode(parsed);
      } catch (e) {
        console.warn("AdminEditBanner: sessionStorage inválido, limpiando", e);
        clearAdminMode();
        setMode(null);
      }
    };
    load();
    const onChanged = () => load();
    window.addEventListener("am-admin-edit-changed", onChanged);
    return () => window.removeEventListener("am-admin-edit-changed", onChanged);
  }, [location.pathname]);

  if (!mode) return null;
  const active = ACTIVE_PREFIXES.some((p) => location.pathname.startsWith(p));
  if (!active) return null;

  const exitMode = () => {
    clearAdminMode();
    window.dispatchEvent(new Event("am-admin-edit-changed"));
    navigate(mode.backTo || "/admin/dashboard");
  };

  const goDetail = () => {
    navigate(`/admin/stand/${mode.standId}`);
  };

  return (
    <div data-testid="admin-edit-banner" className="sticky top-0 z-50 w-full bg-[#FF5A00] text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center gap-3 text-xs sm:text-sm">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <div className="flex-1 min-w-[200px]">
          <span className="font-bold uppercase tracking-widest">Modo Administrador</span>
          <span className="mx-2 opacity-70">·</span>
          <span>
            Estás modificando el espacio <b className="font-mono">{mode.standNumber}</b> en representación del expositor
            {mode.company ? <> · <span className="opacity-90">{mode.company}</span></> : null}
          </span>
          {mode.warning && (
            <div className="mt-1 text-[11px] bg-amber-950/40 rounded px-2 py-1 max-w-3xl">
              ⚠ {mode.warning}
            </div>
          )}
        </div>
        <button
          data-testid="admin-edit-back-btn"
          onClick={exitMode}
          className="bg-white/15 hover:bg-white/25 rounded-sm px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 transition"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Volver al Panel
        </button>
        <button
          data-testid="admin-edit-detail-btn"
          onClick={goDetail}
          className="bg-white/15 hover:bg-white/25 rounded-sm px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 transition"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Ver datos del stand
        </button>
      </div>
    </div>
  );
}
